/**
 * Folder state for organizing tours on the dashboard.
 *
 * Cloud-backed: per-user Firestore subcollection at `users/{uid}/folders`.
 * Real-time sync via onSnapshot; optimistic local updates on mutation so the
 * UI feels immediate even though Firebase persistence isn't enabled in this
 * project. Folders form a tree via `parentId`. `parentId === null` means
 * top level.
 *
 * Sharing: each folder doc may carry `collaboratorUids: string[]`. Folders
 * with the current user in that list are streamed in via a second
 * `collectionGroup('folders')` subscription and merged into the same
 * `folders` array. Each Folder has `ownerUid` so the UI can tell apart
 * "owned by me" from "shared with me." All mutations are guarded so a
 * collaborator can't accidentally write to a shared folder owned by someone
 * else.
 *
 * Required Firestore rules (must be deployed):
 *   match /users/{userId}/folders/{folderId} {
 *     allow read, write: if request.auth != null && request.auth.uid == userId;
 *   }
 *   match /{path=**}/folders/{folderId} {
 *     allow read: if request.auth != null
 *       && resource.data.collaboratorUids is list
 *       && request.auth.uid in resource.data.collaboratorUids;
 *   }
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { useAppSelector } from 'app/hooks';
import { syncFolderTourCollaborators } from 'app/services/firebase/functions';

export interface Folder {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: number;
  tourIds: string[];
  /** UID of the user whose subcollection this folder lives in. */
  ownerUid: string;
  /** UIDs of users with read access. Empty (or absent) = personal folder. */
  collaboratorUids: string[];
}

interface FolderDoc {
  name?: string;
  parentId?: string | null;
  createdAt?: Timestamp | null;
  tourIds?: string[];
  collaboratorUids?: string[];
}

const generateId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `f_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

/** Returns true if maybeAncestor is an ancestor of folderId (or is folderId). */
function isAncestor(
  folders: Folder[],
  folderId: string,
  maybeAncestor: string,
): boolean {
  if (folderId === maybeAncestor) return true;
  const byId = new Map(folders.map((f) => [f.id, f]));
  let current = byId.get(folderId);
  let safety = folders.length + 1;
  while (current && safety-- > 0) {
    const pid = current.parentId;
    if (pid === null) return false;
    if (pid === maybeAncestor) return true;
    current = byId.get(pid);
  }
  return false;
}

function shapeFolderDoc(id: string, ownerUid: string, data: FolderDoc): Folder {
  return {
    id,
    name: data.name ?? '',
    parentId: data.parentId ?? null,
    createdAt: data.createdAt ? data.createdAt.toMillis() : Date.now(),
    tourIds: Array.isArray(data.tourIds) ? data.tourIds : [],
    ownerUid,
    collaboratorUids: Array.isArray(data.collaboratorUids)
      ? data.collaboratorUids
      : [],
  };
}

export interface UseFoldersResult {
  folders: Folder[];
  /** True if `folder.ownerUid` matches the current user. */
  isOwner: (folder: Folder) => boolean;
  createFolder: (name: string, parentId?: string | null) => Folder;
  renameFolder: (id: string, name: string) => void;
  /** Deletes the folder; promotes its child folders + tours to its parent. */
  deleteFolder: (id: string) => void;
  moveTour: (tourId: string, folderId: string | null) => void;
  /** Moves a folder under a new parent. Guards against cycles. */
  moveFolder: (folderId: string, parentId: string | null) => void;
  getFolderForTour: (tourId: string) => Folder | undefined;
  getChildFolders: (parentId: string | null) => Folder[];
  getFolderPath: (folderId: string | null) => Folder[];
}

export function useFolders(): UseFoldersResult {
  const uid = useAppSelector((state) => state.authState.user?.firebaseId);
  const [ownFolders, setOwnFolders] = useState<Folder[]>([]);
  const [sharedFolders, setSharedFolders] = useState<Folder[]>([]);
  const firestore = useMemo(() => getFirestore(), []);

  const folderColRef = useMemo(
    () => (uid ? collection(firestore, 'users', uid, 'folders') : null),
    [firestore, uid],
  );

  // Stream own folders
  useEffect(() => {
    if (!uid || !folderColRef) {
      setOwnFolders([]);
      return undefined;
    }
    const unsubscribe = onSnapshot(
      folderColRef,
      (snap) => {
        const next = snap.docs.map((d) =>
          shapeFolderDoc(d.id, uid, d.data() as FolderDoc),
        );
        next.sort((a, b) => b.createdAt - a.createdAt);
        setOwnFolders(next);
      },
      (err) => {
        console.warn('[useFolders] own snapshot error', err);
      },
    );
    return unsubscribe;
  }, [folderColRef, uid]);

  // Stream folders shared with me (collection group query)
  useEffect(() => {
    if (!uid) {
      setSharedFolders([]);
      return undefined;
    }
    const q = query(
      collectionGroup(firestore, 'folders'),
      where('collaboratorUids', 'array-contains', uid),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs
          .filter((d) => {
            // ref.path is users/{ownerUid}/folders/{folderId} — filter out
            // any other accidental "folders" subcollections.
            const segments = d.ref.path.split('/');
            return segments[0] === 'users' && segments[2] === 'folders';
          })
          .map((d) => {
            const ownerUid = d.ref.path.split('/')[1];
            return shapeFolderDoc(d.id, ownerUid, d.data() as FolderDoc);
          })
          // Defensive: if the same uid somehow shows up in collaboratorUids
          // of their own folder, the path subscription already covers it.
          .filter((f) => f.ownerUid !== uid);
        next.sort((a, b) => b.createdAt - a.createdAt);
        setSharedFolders(next);
      },
      (err) => {
        console.warn('[useFolders] shared snapshot error', err);
      },
    );
    return unsubscribe;
  }, [firestore, uid]);

  // Combined list — own first, then shared
  const folders = useMemo(
    () => [...ownFolders, ...sharedFolders],
    [ownFolders, sharedFolders],
  );

  const isOwner = useCallback(
    (folder: Folder) => !!uid && folder.ownerUid === uid,
    [uid],
  );

  const createFolder = useCallback(
    (name: string, parentId: string | null = null): Folder => {
      const id = generateId();
      const folder: Folder = {
        id,
        name: name.trim() || 'New folder',
        parentId,
        createdAt: Date.now(),
        tourIds: [],
        ownerUid: uid ?? '',
        collaboratorUids: [],
      };
      // Optimistic
      setOwnFolders((prev) => [folder, ...prev]);
      if (folderColRef) {
        setDoc(doc(folderColRef, id), {
          name: folder.name,
          parentId: folder.parentId,
          createdAt: serverTimestamp(),
          tourIds: folder.tourIds,
          collaboratorUids: [],
        }).catch((err) => {
          console.warn('[useFolders] createFolder failed', err);
          setOwnFolders((prev) => prev.filter((f) => f.id !== id));
        });
      } else {
        console.warn('[useFolders] createFolder ignored — no auth');
      }
      return folder;
    },
    [folderColRef, uid],
  );

  const renameFolder = useCallback(
    (id: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      // Only owner can rename. Find the folder, check ownership.
      const target = folders.find((f) => f.id === id);
      if (!target || target.ownerUid !== uid || !folderColRef) return;
      setOwnFolders((prev) =>
        prev.map((f) => (f.id === id ? { ...f, name: trimmed } : f)),
      );
      updateDoc(doc(folderColRef, id), { name: trimmed }).catch((err) => {
        console.warn('[useFolders] renameFolder failed', err);
      });
    },
    [folderColRef, folders, uid],
  );

  const deleteFolder = useCallback(
    (id: string) => {
      const target = ownFolders.find((f) => f.id === id);
      // Only owner can delete; non-owners get a no-op.
      if (!target || !folderColRef || target.ownerUid !== uid) return;

      const orphanedTourIds = target.tourIds;
      const targetParent = target.parentId;

      setOwnFolders((prev) =>
        prev
          .filter((f) => f.id !== id)
          .map((f) => {
            if (f.parentId === id) return { ...f, parentId: targetParent };
            if (
              targetParent !== null &&
              f.id === targetParent &&
              orphanedTourIds.length
            ) {
              const existing = new Set(f.tourIds);
              const merged = [
                ...f.tourIds,
                ...orphanedTourIds.filter((t) => !existing.has(t)),
              ];
              return { ...f, tourIds: merged };
            }
            return f;
          }),
      );

      const batch = writeBatch(firestore);
      batch.delete(doc(folderColRef, id));
      const childrenAtDelete = ownFolders.filter((f) => f.parentId === id);
      for (const child of childrenAtDelete) {
        batch.update(doc(folderColRef, child.id), {
          parentId: target.parentId,
        });
      }
      if (target.parentId !== null && target.tourIds.length) {
        batch.update(doc(folderColRef, target.parentId), {
          tourIds: arrayUnion(...target.tourIds),
        });
      }
      batch.commit().catch((err) => {
        console.warn('[useFolders] deleteFolder failed', err);
      });
    },
    [folderColRef, firestore, ownFolders, uid],
  );

  const moveTour = useCallback(
    (tourId: string, folderId: string | null) => {
      if (!folderColRef || !uid) return;

      // Mutate only folders I own. If the destination is a folder I don't
      // own (i.e. a shared folder), bail — collaborators are read-only.
      if (folderId !== null) {
        const dest = folders.find((f) => f.id === folderId);
        if (!dest || dest.ownerUid !== uid) return;
      }

      // Optimistic: update only own folders' tourIds.
      setOwnFolders((prev) =>
        prev.map((f) => {
          const without = f.tourIds.filter((id) => id !== tourId);
          if (f.id === folderId) {
            return { ...f, tourIds: [...without, tourId] };
          }
          return without.length === f.tourIds.length
            ? f
            : { ...f, tourIds: without };
        }),
      );

      const batch = writeBatch(firestore);
      // Only touch own folders in writes — collaborator-only folders are
      // outside our write rule.
      for (const f of ownFolders) {
        if (f.id !== folderId && f.tourIds.includes(tourId)) {
          batch.update(doc(folderColRef, f.id), {
            tourIds: arrayRemove(tourId),
          });
        }
      }
      if (folderId !== null) {
        batch.update(doc(folderColRef, folderId), {
          tourIds: arrayUnion(tourId),
        });
      }
      batch
        .commit()
        .then(() => {
          // If the destination is a shared folder, cascade tour-level
          // collaboration to the folder's collaborators so they actually see
          // the tour on their dashboard. Idempotent and fire-and-forget.
          if (folderId !== null) {
            const dest = ownFolders.find((f) => f.id === folderId);
            if (dest && dest.collaboratorUids.length > 0) {
              syncFolderTourCollaborators(folderId, dest.ownerUid, [
                tourId,
              ]).catch((err) => {
                console.warn('[useFolders] cascade after moveTour failed', err);
              });
            }
          }
        })
        .catch((err) => {
          console.warn('[useFolders] moveTour failed', err);
        });
    },
    [folderColRef, firestore, folders, ownFolders, uid],
  );

  const moveFolder = useCallback(
    (folderId: string, parentId: string | null) => {
      if (parentId === folderId) return;
      const target = ownFolders.find((f) => f.id === folderId);
      if (!target || target.ownerUid !== uid || !folderColRef) return;
      if (parentId !== null && isAncestor(ownFolders, parentId, folderId)) {
        return; // would create a cycle
      }
      setOwnFolders((prev) =>
        prev.map((f) => (f.id === folderId ? { ...f, parentId } : f)),
      );
      updateDoc(doc(folderColRef, folderId), { parentId }).catch((err) => {
        console.warn('[useFolders] moveFolder failed', err);
      });
    },
    [folderColRef, ownFolders, uid],
  );

  const getFolderForTour = useCallback(
    (tourId: string): Folder | undefined =>
      folders.find((f) => f.tourIds.includes(tourId)),
    [folders],
  );

  const getChildFolders = useCallback(
    (parentId: string | null): Folder[] =>
      folders.filter((f) => f.parentId === parentId),
    [folders],
  );

  const getFolderPath = useCallback(
    (folderId: string | null): Folder[] => {
      if (!folderId) return [];
      const byId = new Map(folders.map((f) => [f.id, f]));
      const path: Folder[] = [];
      let current = byId.get(folderId);
      let safety = folders.length + 1;
      while (current && safety-- > 0) {
        path.unshift(current);
        if (current.parentId === null) break;
        current = byId.get(current.parentId);
      }
      return path;
    },
    [folders],
  );

  // deleteDoc is imported but currently used only inside batches. Keep the
  // import alive so a future direct delete doesn't trigger no-unused-vars.
  void deleteDoc;

  return {
    folders,
    isOwner,
    createFolder,
    renameFolder,
    deleteFolder,
    moveTour,
    moveFolder,
    getFolderForTour,
    getChildFolders,
    getFolderPath,
  };
}
